import type { WholeStoryType } from "@/config";
import { handleFetch } from "@/shared";
import {
  useCreateVersionMutation,
  useUpdateVersionMutation,
} from "@api/versions";
import { useAppDispatch } from "@store";
import { setOverrideContentPointer } from "@store/share-dialog";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { useGeneratePreviewLink } from "./use-generate-preview-link";

interface CreateVersionParams {
  pointer: string | null;
  expireAt: string;
  isPublic: boolean;
  data: Record<string, unknown>;
}

const DEFAULT_OG =
  "https://www.storydoc.com/assets/images/branding/og-storydoc.gif";

export const useCreateVersionFromPointer = () => {
  const [createFromPointerLoading, setCreateFromPointerLoading] =
    useState(false);
  const handlePreviewLink = useGeneratePreviewLink();
  const [updateVersion] = useUpdateVersionMutation();
  const [createVersion] = useCreateVersionMutation();
  const dispatch = useAppDispatch();

  const handleCreateVersionFromPointer = useCallback(
    async ({ pointer, expireAt, isPublic, data }: CreateVersionParams) => {
      if (!pointer) return null;

      setCreateFromPointerLoading(true);
      try {
        const [story, error] = await handleFetch(pointer);
        if (error || !story) throw error;

        const version = await createVersion({
          id: story?._id,
          data: {
            ...data,
            ogImage: DEFAULT_OG,
            sdScreenshotLink: DEFAULT_OG,
          },
          expireAt,
          isPublic,
          contentPointer: pointer,
        }).unwrap();

        await handlePreviewLink(
          story as WholeStoryType,
          version._id,
          false,
          data,
        );

        await updateVersion({
          versionId: version._id,
          payload: {},
        }).unwrap();
        setCreateFromPointerLoading(false);
        dispatch(setOverrideContentPointer(null));
        return version;
      } catch (error) {
        console.error("Error creating version from pointer:", error);
        toast.error("Failed to duplicate version from history");
        setCreateFromPointerLoading(false);
        dispatch(setOverrideContentPointer(null));
        return null;
      }
    },
    [createVersion, updateVersion, handlePreviewLink, dispatch],
  );

  return { handleCreateVersionFromPointer, createFromPointerLoading };
};
